// ThemisRuleEffectsBuilder.tsx
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Button from "../../../components/Button/Button";
import TextField from "../../../components/FormFields/TextField";
import Icon from "../../../components/Icon/Icon";
import { useAlert } from "../../../components/Alert/Alerts";
import { SelectBox } from "../Judging/JudgingConfig/EntrySetTab";

import {
	createEffect,
	deleteEffect,
	updateEffect,
	ThemisEffectKind,
} from "./manageThemisRuleEffects";
import { ThemisPickFieldFn } from "./themisFieldPickerTypes";
import ThemisFieldPickerButton from "./ThemisFieldPickerButton";

import {
	ThemisConditionValueType,
	ThemisEffectOperation,
	ThemisFieldKeyEffectTarget,
} from "./ThemisInterfaces";

type ThemisEffectDto = {
	id: number;
	themisRuleId: number;

	kind: "Price" | "FieldKey" | "Display" | "Time"; // from API
	name: string;
	label: string;
	description: string;
	phase: string;
	operation: number;

	priceValue?: number | null;
	timeValue?: number | null;
	displayValue?: boolean | null;

	fieldKeyValue?: string | null;
	fieldKeyValueType?: number | null;
	fieldKeyTarget?: number | null;
};

type Props = {
	ruleId: number;
	phaseKey: string;
	targetScopeKey: string;
	effects: ThemisEffectDto[];
	pickField: ThemisPickFieldFn;
	onReload(): Promise<void>;
};

const Wrap = styled.section`
	width: 100%;
	padding: 1.5rem;
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLightLight};
	box-shadow: 0 2px 10px 0 ${({ theme }) => theme.colorBoxShadow};
`;

const PanelTitle = styled.h3`
	margin: 0 0 0.25rem 0;
`;

const Muted = styled.p`
	font-size: 0.9rem;
	opacity: 0.7;
	margin: 0;
	max-width: 44rem;
`;

const HeaderBar = styled.div`
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1.25rem;
	flex-wrap: wrap;
`;

const ActionCluster = styled.div`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
	justify-content: flex-end;
`;

const EffectCard = styled.div`
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLightLight};
`;

const EffectHeader = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	padding: 1rem;
	border-bottom: 1px solid ${({ theme }) => theme.colorBorderLight};
`;

const HeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

const EffectHeading = styled.div`
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
`;

const BadgeRow = styled.div`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
	align-items: center;
`;

const Badge = styled.span`
	display: inline-flex;
	align-items: center;
	min-height: 1.6rem;
	padding: 0.15rem 0.55rem;
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLight};
	font-size: 0.74rem;
	font-weight: 700;
	letter-spacing: 0.05em;
	text-transform: uppercase;
`;

const EffectHeaderText = styled.div`
	font-size: 0.92rem;
	opacity: 0.75;
`;

const EffectBody = styled.div`
	padding: 1rem;
	display: flex;
	flex-direction: column;
	gap: 1rem;
	align-items: flex-start;

	> * {
		width: 100%;
		max-width: 1100px;
	}
`;

const GridTwo = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 0.75rem;

	@media only screen and (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`;

const FieldKeyRow = styled.div`
	display: grid;
	grid-template-columns: 240px 180px minmax(0, 1fr);
	gap: 0.75rem;
	align-items: end;

	@media only screen and (max-width: 1100px) {
		grid-template-columns: 1fr;
	}
`;

const FieldStack = styled.div`
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.45rem;
`;

const FieldLabel = styled.label`
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	opacity: 0.62;
`;

const SelectField = styled.select`
	width: 100%;
	min-height: 50px;
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLight};
	padding: 0 0.75rem;
	box-shadow: 0 2px 8px 0 ${({ theme }) => theme.colorBoxShadow};
`;

const ConfigPanel = styled.div`
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLight};
	padding: 0.9rem;
	display: flex;
	flex-direction: column;
	gap: 0.9rem;
`;

const ConfigHint = styled.div`
	font-size: 0.86rem;
	opacity: 0.74;
`;

const IconButton = styled.button`
	width: 42px;
	height: 42px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLight};
	box-shadow: 0 2px 8px 0 ${({ theme }) => theme.colorBoxShadow};
	transition:
		border-color 140ms ease-in,
		background 140ms ease-in;

	&:hover {
		border-color: ${({ theme }) => theme.colorActivation};
		background: ${({ theme }) => theme.colorBackgroundLightLight};
	}

	align-self: start;
`;

const intToOperation = (n: number): ThemisEffectOperation => {
	switch (n) {
		case 0:
			return ThemisEffectOperation.Addition;
		case 1:
			return ThemisEffectOperation.Subtraction;
		case 2:
			return ThemisEffectOperation.Multiplication;
		case 3:
			return ThemisEffectOperation.Set;
		case 4:
			return ThemisEffectOperation.Limit;
		case 5:
			return ThemisEffectOperation.Cap;
		default:
			return ThemisEffectOperation.Addition;
	}
};

const valueTypeToInt = (vt: ThemisConditionValueType): number => {
	switch (vt) {
		case ThemisConditionValueType.String:
			return 0;
		case ThemisConditionValueType.Number:
			return 1;
		case ThemisConditionValueType.Boolean:
			return 2;
		case ThemisConditionValueType.DateTimeUtcMs:
			return 3;
		case ThemisConditionValueType.Int:
			return 4;
		case ThemisConditionValueType.Decimal:
			return 5;
		default:
			return 5;
	}
};

const intToFieldKeyTarget = (n: number): ThemisFieldKeyEffectTarget => {
	switch (n) {
		case 0:
			return ThemisFieldKeyEffectTarget.LineSubtotal;
		case 1:
			return ThemisFieldKeyEffectTarget.LineDiscountTotal;
		case 2:
			return ThemisFieldKeyEffectTarget.LineFeeTotal;
		case 3:
			return ThemisFieldKeyEffectTarget.LineTaxRate;
		case 4:
			return ThemisFieldKeyEffectTarget.LineTaxAmount;
		case 5:
			return ThemisFieldKeyEffectTarget.LineEventNow;

		case 20:
			return ThemisFieldKeyEffectTarget.TargetDiscountTotal;
		case 21:
			return ThemisFieldKeyEffectTarget.TargetFeeTotal;
		case 22:
			return ThemisFieldKeyEffectTarget.TargetTaxRate;
		case 23:
			return ThemisFieldKeyEffectTarget.TargetTaxAmount;
		case 24:
			return ThemisFieldKeyEffectTarget.TargetEventNow;

		default:
			return ThemisFieldKeyEffectTarget.LineSubtotal;
	}
};

const fieldKeyTargetToInt = (v: ThemisFieldKeyEffectTarget): number => {
	switch (v) {
		case ThemisFieldKeyEffectTarget.LineSubtotal:
			return 0;
		case ThemisFieldKeyEffectTarget.LineDiscountTotal:
			return 1;
		case ThemisFieldKeyEffectTarget.LineFeeTotal:
			return 2;
		case ThemisFieldKeyEffectTarget.LineTaxRate:
			return 3;
		case ThemisFieldKeyEffectTarget.LineTaxAmount:
			return 4;
		case ThemisFieldKeyEffectTarget.LineEventNow:
			return 5;
		case ThemisFieldKeyEffectTarget.TargetDiscountTotal:
			return 20;
		case ThemisFieldKeyEffectTarget.TargetFeeTotal:
			return 21;
		case ThemisFieldKeyEffectTarget.TargetTaxRate:
			return 22;
		case ThemisFieldKeyEffectTarget.TargetTaxAmount:
			return 23;
		case ThemisFieldKeyEffectTarget.TargetEventNow:
			return 24;
		default:
			return 0;
	}
};

const getValueTypeForFieldKeyTarget = (
	target: ThemisFieldKeyEffectTarget,
): ThemisConditionValueType => {
	switch (target) {
		case ThemisFieldKeyEffectTarget.LineEventNow:
		case ThemisFieldKeyEffectTarget.TargetEventNow:
			return ThemisConditionValueType.DateTimeUtcMs;
		default:
			return ThemisConditionValueType.Decimal;
	}
};

const operationOptions = Object.values(ThemisEffectOperation);

const fieldKeyTargetOptions = Object.values(ThemisFieldKeyEffectTarget);

export default function ThemisRuleEffectsBuilder({
	ruleId,
	phaseKey,
	targetScopeKey,
	effects,
	pickField,
	onReload,
}: Props) {
	const { addNewAlert } = useAlert();

	const sortedEffects = useMemo(() => {
		return effects.slice().sort((a, b) => a.id - b.id);
	}, [effects]);

	// Drafts so we don't call API on every keystroke
	const [drafts, setDrafts] = useState<
		Record<number, Partial<ThemisEffectDto>>
	>({});
	const [activePickerEffectId, setActivePickerEffectId] = useState<
		number | null
	>(null);

	useEffect(() => {
		const next: Record<number, Partial<ThemisEffectDto>> = {};
		effects.forEach((e) => {
			next[e.id] = {
				name: e.name,
				label: e.label,
				description: e.description,
				operation: e.operation,
				priceValue: e.priceValue ?? null,
				fieldKeyValue: e.fieldKeyValue ?? "",
				fieldKeyValueType: e.fieldKeyValueType ?? 5, // default Decimal
				fieldKeyTarget:
					e.fieldKeyTarget ?? (targetScopeKey === "Order" ? 21 : 0),
			};
		});
		setDrafts(next);
	}, [effects, targetScopeKey]);

	const createPriceEffect = async () => {
		try {
			await createEffect(ruleId, {
				kind: ThemisEffectKind.Price,
				name: "NewPriceEffect",
				label: "New Price Effect",
				description: "",
				phase: phaseKey,
				operation: ThemisEffectOperation.Addition,
				priceValue: 0,
			});
			addNewAlert({ type: "success", message: "Effect created." });
			await onReload();
		} catch {
			addNewAlert({ type: "error", message: "Failed to create effect." });
		}
	};

	const createFieldKeyEffect = async () => {
		try {
			const defaultTarget =
				targetScopeKey === "Order"
					? ThemisFieldKeyEffectTarget.TargetFeeTotal
					: ThemisFieldKeyEffectTarget.LineSubtotal;
			await createEffect(ruleId, {
				kind: ThemisEffectKind.FieldKey,
				name: "NewFieldKeyEffect",
				label: "New FieldKey Effect",
				description: "",
				phase: phaseKey,
				operation: ThemisEffectOperation.Set,
				fieldKeyValue: "",
				fieldKeyValueType: getValueTypeForFieldKeyTarget(defaultTarget),
				fieldKeyTarget: defaultTarget,
			});
			addNewAlert({ type: "success", message: "Effect created." });
			await onReload();
		} catch {
			addNewAlert({ type: "error", message: "Failed to create effect." });
		}
	};

	const saveEffect = async (e: ThemisEffectDto) => {
		const d = drafts[e.id] ?? {};

		try {
			const operation = intToOperation((d.operation ?? e.operation) as number);

			if (e.kind === "Price") {
				await updateEffect(e.id, {
					name: (d.name ?? e.name) as string,
					label: (d.label ?? e.label) as string,
					description: (d.description ?? e.description) as string,
					operation,
					priceValue: Number(d.priceValue ?? e.priceValue ?? 0),
					timeValue: null,
					displayValue: null,
					fieldKeyValue: null,
					fieldKeyValueType: null,
					fieldKeyTarget: null,
				});
			} else if (e.kind === "FieldKey") {
				const fkv = (d.fieldKeyValue ?? e.fieldKeyValue ?? "") as string;
				const fkt = intToFieldKeyTarget(
					(d.fieldKeyTarget ?? e.fieldKeyTarget ?? 0) as number,
				);
				const fkvt = getValueTypeForFieldKeyTarget(fkt);

				await updateEffect(e.id, {
					name: (d.name ?? e.name) as string,
					label: (d.label ?? e.label) as string,
					description: (d.description ?? e.description) as string,
					operation,
					priceValue: null,
					timeValue: null,
					displayValue: null,
					fieldKeyValue: fkv,
					fieldKeyValueType: fkvt,
					fieldKeyTarget: fkt,
				});
			} else {
				addNewAlert({ type: "error", message: "Unsupported effect type." });
				return;
			}

			addNewAlert({ type: "success", message: "Effect saved." });
			await onReload();
		} catch {
			addNewAlert({ type: "error", message: "Failed to save effect." });
		}
	};

	const removeEffect = async (id: number) => {
		try {
			await deleteEffect(id);
			addNewAlert({ type: "success", message: "Effect deleted." });
			await onReload();
		} catch {
			addNewAlert({ type: "error", message: "Failed to delete effect." });
		}
	};

	const pickFieldForEffect = async (
		effectId: number,
		valueType: ThemisConditionValueType,
	) => {
		setActivePickerEffectId(effectId);
		try {
			const field = await pickField({
				title: "Select Field Key",
				requiredValueType: valueType,
			});
			if (!field) return;

			setDrafts((prev) => ({
				...prev,
				[effectId]: {
					...prev[effectId],
					fieldKeyValue: field.fieldKey,
					fieldKeyValueType: valueTypeToInt(valueType),
				},
			}));
		} finally {
			setActivePickerEffectId((current) =>
				current === effectId ? null : current,
			);
		}
	};

	return (
		<Wrap>
			<HeaderBar>
				<div>
					<PanelTitle>Effects</PanelTitle>
					<Muted>
						Define the pricing changes this rule applies after its conditions
						match. Only Price and FieldKey effects are currently supported in{" "}
						{phaseKey}.
					</Muted>
				</div>
			</HeaderBar>

			<div className="flex flex-col gap-[1rem]">
				{sortedEffects.length === 0 ? (
					<SelectBox>No effects yet. Add one to start.</SelectBox>
				) : (
					<>
						{sortedEffects.map((e) => {
							const d = drafts[e.id] ?? {};
							const op = (d.operation ?? e.operation) as number;
							const fieldTarget = intToFieldKeyTarget(
								(d.fieldKeyTarget ??
									e.fieldKeyTarget ??
									(targetScopeKey === "Order" ? 21 : 0)) as number,
							);
							const fieldValueType = getValueTypeForFieldKeyTarget(fieldTarget);
							const effectLabel = (d.label ?? e.label ?? "") as string;
							const effectName = (d.name ?? e.name ?? "") as string;
							const effectDescription = (d.description ??
								e.description ??
								"") as string;
							const currentOperation = intToOperation(op);

							return (
								<EffectCard key={e.id}>
									<EffectHeader>
										<EffectHeading>
											<BadgeRow>
												<Badge>{e.kind} Effect</Badge>
												<Badge>ID {e.id}</Badge>
											</BadgeRow>
											<div className="font-semibold">
												{effectLabel || effectName || `Effect ${e.id}`}
											</div>
											<EffectHeaderText>
												{e.kind === "FieldKey"
													? "Copy a value from a catalog field into a pricing target."
													: "Apply a numeric operation directly to pricing state."}
											</EffectHeaderText>
										</EffectHeading>

										<HeaderActions>
											<Button
												className="button-light"
												onClick={() => void saveEffect(e)}
											>
												Save
											</Button>
											<IconButton
												type="button"
												aria-label={`Delete effect ${e.id}`}
												onClick={() => void removeEffect(e.id)}
											>
												<Icon
													icon="trash"
													color="currentColor"
													width="18px"
													height="18px"
												/>
											</IconButton>
										</HeaderActions>
									</EffectHeader>

									<EffectBody>
										<GridTwo>
											<TextField
												name={`eff_name_${e.id}`}
												placeholder="Internal name"
												value={effectName}
												onChange={(ev) =>
													setDrafts((prev) => ({
														...prev,
														[e.id]: { ...prev[e.id], name: ev.target.value },
													}))
												}
											/>
											<TextField
												name={`eff_label_${e.id}`}
												placeholder="Admin label"
												value={effectLabel}
												onChange={(ev) =>
													setDrafts((prev) => ({
														...prev,
														[e.id]: { ...prev[e.id], label: ev.target.value },
													}))
												}
											/>
										</GridTwo>

										<TextField
											name={`eff_desc_${e.id}`}
											placeholder="Description"
											component="textarea"
											height="6rem"
											value={effectDescription}
											onChange={(ev) =>
												setDrafts((prev) => ({
													...prev,
													[e.id]: {
														...prev[e.id],
														description: ev.target.value,
													},
												}))
											}
										/>

										{e.kind === "Price" ? (
											<ConfigPanel>
												<ConfigHint>
													Choose the math operation, then provide the numeric
													value to apply.
												</ConfigHint>

												<GridTwo>
													<FieldStack>
														<FieldLabel>Operation</FieldLabel>
														<SelectField
															value={currentOperation}
															onChange={(ev) => {
																const selected = ev.target
																	.value as ThemisEffectOperation;
																const toInt = (v: ThemisEffectOperation) =>
																	operationOptions.indexOf(v) >= 0
																		? operationOptions.indexOf(v)
																		: 0;

																setDrafts((prev) => ({
																	...prev,
																	[e.id]: {
																		...prev[e.id],
																		operation: toInt(selected),
																	},
																}));
															}}
														>
															{operationOptions.map((o) => (
																<option key={o} value={o}>
																	{o}
																</option>
															))}
														</SelectField>
													</FieldStack>

													<FieldStack>
														<FieldLabel>Price Value</FieldLabel>
														<TextField
															name={`eff_price_${e.id}`}
															placeholder="0.00"
															value={String(d.priceValue ?? e.priceValue ?? "")}
															onChange={(ev) =>
																setDrafts((prev) => ({
																	...prev,
																	[e.id]: {
																		...prev[e.id],
																		priceValue:
																			ev.target.value === ""
																				? null
																				: Number(ev.target.value),
																	},
																}))
															}
														/>
													</FieldStack>
												</GridTwo>
											</ConfigPanel>
										) : null}

										{e.kind === "FieldKey" ? (
											<ConfigPanel>
												<ConfigHint>
													Select the target pricing field first, then choose the
													operation and source field.
												</ConfigHint>

												<FieldKeyRow>
													<FieldStack>
														<FieldLabel>Target</FieldLabel>
														<SelectField
															value={fieldTarget}
															onChange={(ev) => {
																const picked = ev.target
																	.value as ThemisFieldKeyEffectTarget;
																const nextValueType =
																	getValueTypeForFieldKeyTarget(picked);
																setDrafts((prev) => ({
																	...prev,
																	[e.id]: {
																		...prev[e.id],
																		fieldKeyTarget: fieldKeyTargetToInt(picked),
																		fieldKeyValueType:
																			valueTypeToInt(nextValueType),
																	},
																}));
															}}
														>
															{fieldKeyTargetOptions.map((t) => (
																<option key={t} value={t}>
																	{t}
																</option>
															))}
														</SelectField>
													</FieldStack>

													<FieldStack>
														<FieldLabel>Operation</FieldLabel>
														<SelectField
															value={currentOperation}
															onChange={(ev) => {
																const selected = ev.target
																	.value as ThemisEffectOperation;
																const toInt = (v: ThemisEffectOperation) =>
																	operationOptions.indexOf(v) >= 0
																		? operationOptions.indexOf(v)
																		: 0;

																setDrafts((prev) => ({
																	...prev,
																	[e.id]: {
																		...prev[e.id],
																		operation: toInt(selected),
																	},
																}));
															}}
														>
															{operationOptions.map((o) => (
																<option key={o} value={o}>
																	{o}
																</option>
															))}
														</SelectField>
													</FieldStack>

													<FieldStack>
														<FieldLabel>Source Field</FieldLabel>
														<ThemisFieldPickerButton
															value={
																((d.fieldKeyValue ??
																	e.fieldKeyValue ??
																	"") as string) || null
															}
															placeholder="Select source field"
															active={activePickerEffectId === e.id}
															onClick={() =>
																void pickFieldForEffect(e.id, fieldValueType)
															}
														/>
													</FieldStack>
												</FieldKeyRow>
											</ConfigPanel>
										) : null}
									</EffectBody>
								</EffectCard>
							);
						})}
					</>
				)}

				<ActionCluster>
					<Button
						className="button-light"
						onClick={() => void createPriceEffect()}
					>
						Add Price Effect
					</Button>
					<Button icon="plus" onClick={() => void createFieldKeyEffect()}>
						Add FieldKey Effect
					</Button>
				</ActionCluster>
			</div>
		</Wrap>
	);
}
